/* shaderBg.ts
 *
 * <shader-bg> — a dependency-free WebGL paint shader.
 *
 * Domain-warped noise marbled through the brand palette. Two modes:
 *
 *   <shader-bg words="DESIGN|BUILD|SHIP|SCALE">  the fluid renders *through*
 *       the letterforms (the words are drawn to an offscreen 2d canvas with
 *       the page's real fonts and uploaded as an alpha mask), and
 *       setProgress() slides the paint field underneath the static glyphs —
 *       the parallax-through-text effect.
 *
 *   <shader-bg>  full-bleed fluid, for video-style panel backgrounds.
 *
 * Renders only while on screen, caps DPR at 1.5, and collapses to a single
 * static frame for prefers-reduced-motion.
 */

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
    v_uv = a_pos * 0.5 + 0.5;
    gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAG = `
precision highp float;

uniform vec2  u_res;
uniform float u_time;
uniform float u_par;
uniform float u_vel;
uniform float u_hasMask;
uniform sampler2D u_mask;

varying vec2 v_uv;

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i),                 hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
        v += a * noise(p);
        p = p * 2.03 + vec2(11.7, 5.3);
        a *= 0.5;
    }
    return v;
}

void main() {
    vec2 q = v_uv;
    q.x *= u_res.x / u_res.y;

    float t = u_time * 0.055 + u_vel * 0.35;

    // the paint field slides on u_par while the mask stays put
    vec2 flow = q * 1.55 + vec2(0.0, u_par * 1.6);

    float w1 = fbm(flow + vec2(t * 0.7, -t * 0.9));
    float w2 = fbm(flow * 1.7 + vec2(w1 * 1.9, t * 0.55));
    float f  = fbm(flow * 1.15 + vec2(w2 * 2.3 - t * 0.3, w1 * 1.7));

    vec3 cBlack = vec3(0.047, 0.047, 0.047);
    vec3 cRed   = vec3(0.682, 0.169, 0.118);
    vec3 cOx    = vec3(0.380, 0.090, 0.063);
    vec3 cCream = vec3(0.910, 0.902, 0.843);

    vec3 col = cRed;
    col = mix(col, cBlack, smoothstep(0.30, 0.14, f));
    col = mix(col, cOx,    smoothstep(0.44, 0.58, f));
    col = mix(col, cCream, smoothstep(0.76, 0.92, f));

    float m = u_hasMask > 0.5
        ? texture2D(u_mask, vec2(v_uv.x, 1.0 - v_uv.y)).a
        : 1.0;

    gl_FragColor = vec4(col * m, m);
}`;

export class ShaderBg extends HTMLElement {

    private canvas!: HTMLCanvasElement;
    private gl: WebGLRenderingContext | null = null;
    private uniforms: Record<string, WebGLUniformLocation | null> = {};
    private maskTex: WebGLTexture | null = null;

    private rafId = 0;
    private visible = false;
    private startTime = performance.now();
    private progress = 0.5;
    private velocity = 0;
    private reducedMotion = false;
    private observer: IntersectionObserver | null = null;
    private resizer: ResizeObserver | null = null;

    connectedCallback() {
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.canvas = document.createElement('canvas');
        this.appendChild(this.canvas);

        this.gl = this.canvas.getContext('webgl', {
            alpha: true,
            antialias: false,
            premultipliedAlpha: true,
        });

        if (!this.gl) return;   // no WebGL: section copy still reads, just no paint

        this.compile();
        this.fit();

        // glyph metrics change when the real font swaps in
        document.fonts.ready.then(() => {
            this.buildMask();
            this.renderFrame();
        });

        this.resizer = new ResizeObserver(() => {
            this.fit();
            this.renderFrame();
        });
        this.resizer.observe(this);

        this.observer = new IntersectionObserver(([entry]) => {
            this.visible = !!entry?.isIntersecting;
            if (this.visible) this.play();
            else this.pause();
        });
        this.observer.observe(this);
    }

    disconnectedCallback() {
        this.pause();
        this.observer?.disconnect();
        this.resizer?.disconnect();
    }

    /* scroll engine hooks */
    setProgress(p: number) {
        this.progress = p;
        if (this.reducedMotion && this.visible) this.renderFrame();
    }

    setVelocity(v: number) {
        this.velocity = Math.max(-1, Math.min(1, v / 30));
    }

    /* the shell calls this after it has measured the nav and written the
     * --forge-top-safe clear-band reserve, so the glyph block re-lays-out */
    reflow() {
        this.buildMask();
        this.renderFrame();
    }

    private play() {
        if (this.rafId || this.reducedMotion) {
            this.renderFrame();
            if (this.reducedMotion) return;
        }
        const loop = () => {
            this.renderFrame();
            this.rafId = requestAnimationFrame(loop);
        };
        this.rafId = requestAnimationFrame(loop);
    }

    private pause() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = 0;
    }

    private compile() {
        const gl = this.gl!;

        const make = (type: number, src: string) => {
            const s = gl.createShader(type)!;
            gl.shaderSource(s, src);
            gl.compileShader(s);
            return s;
        };

        const prog = gl.createProgram()!;
        gl.attachShader(prog, make(gl.VERTEX_SHADER, VERT));
        gl.attachShader(prog, make(gl.FRAGMENT_SHADER, FRAG));
        gl.linkProgram(prog);
        gl.useProgram(prog);

        const quad = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, quad);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

        const loc = gl.getAttribLocation(prog, 'a_pos');
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

        for (const name of ['u_res', 'u_time', 'u_par', 'u_vel', 'u_hasMask', 'u_mask']) {
            this.uniforms[name] = gl.getUniformLocation(prog, name);
        }
    }

    private fit() {
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        const w = Math.max(1, Math.round(this.clientWidth * dpr));
        const h = Math.max(1, Math.round(this.clientHeight * dpr));
        if (this.canvas.width === w && this.canvas.height === h) return;

        this.canvas.width = w;
        this.canvas.height = h;
        this.gl?.viewport(0, 0, w, h);
        this.buildMask();
    }

    /* draw the words with the page's own fonts, upload the alpha as the mask */
    private buildMask() {
        const gl = this.gl;
        if (!gl) return;

        const words = (this.getAttribute('words') ?? '')
            .split('|')
            .map((w) => w.trim())
            .filter(Boolean);

        if (!words.length) {
            gl.uniform1f(this.uniforms.u_hasMask, 0);
            return;
        }

        const { width: w, height: h } = this.canvas;
        const mask = document.createElement('canvas');
        mask.width = w;
        mask.height = h;
        const ctx = mask.getContext('2d')!;

        // the glyph block lives in the clear band *between* the nav (top) and
        // the caption (bottom) — both reserves are read here so the letterforms
        // never crowd the pill or the strapline. --forge-top-safe is written by
        // the shell once it has measured the real nav height (it tracks the
        // pill's padding); --forge-bottom-safe keeps the caption clear.
        const scaleY = this.canvas.height / Math.max(1, this.clientHeight);
        const readPx = (name: string) =>
            (parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0) * scaleY;
        const safeTop = readPx('--forge-top-safe');
        const safeBottom = readPx('--forge-bottom-safe');
        const availH = Math.max(1, h - safeTop - safeBottom);

        // measure at a trial size, then scale so the widest word spans ~90% of
        // the width and the whole block fits inside ~90% of the clear band
        const TRIAL = 100;
        ctx.font = `${TRIAL}px ArchivoBlack, sans-serif`;
        const widest = Math.max(...words.map((word) => ctx.measureText(word).width));
        const capH = ctx.measureText(words.join('')).actualBoundingBoxAscent;

        let size = (w * 0.9) / (widest / TRIAL);
        const capOf = (s: number) => (capH / TRIAL) * s;
        const blockOf = (s: number) => capOf(s) * (1 + (words.length - 1) * 1.16);
        if (blockOf(size) > availH * 0.9) {
            size *= (availH * 0.9) / blockOf(size);
        }

        ctx.font = `${size}px ArchivoBlack, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#fff';

        const cap = capOf(size);
        const step = cap * 1.16;
        const firstBaseline = safeTop + (availH - blockOf(size)) / 2 + cap;
        words.forEach((word, i) => ctx.fillText(word, w / 2, firstBaseline + step * i));

        if (!this.maskTex) this.maskTex = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.maskTex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.uniform1f(this.uniforms.u_hasMask, 1);
        gl.uniform1i(this.uniforms.u_mask, 0);
    }

    private renderFrame() {
        const gl = this.gl;
        if (!gl) return;

        gl.uniform2f(this.uniforms.u_res, this.canvas.width, this.canvas.height);
        gl.uniform1f(this.uniforms.u_time, (performance.now() - this.startTime) / 1000);
        gl.uniform1f(this.uniforms.u_par, this.progress);
        gl.uniform1f(this.uniforms.u_vel, this.velocity);

        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}

customElements.define('shader-bg', ShaderBg);
